import Vue from 'vue';
import AWS from 'aws-sdk/global';
import 'aws-sdk/clients/firehose';
import 'aws-sdk/lib/credentials/cognito_identity_credentials';
import dateConverter from '../utils/helpers/dateConverter';
import {
    postTelNumber,
    fetchMapDataFromCDN,
    fetchMapDataFromApi,
    fetchMapDataLastModified,
} from '../utils/services';

export const fetchMapData = async ({ commit, dispatch, getters, state }, mapId = null) =>
{
    if(!state.dataSourceType){
        // TODO: Rethink
        let isCDN = process.env.VUE_APP_DATA_SOURCE === 'CDN'
                    && !document.location.href.includes('preview.')
                    && !document.location.href.includes('staging.')
                    && !document.location.href.includes('localhost')

        if(document.location.href.includes('dataSource'))
            isCDN = document.location.href.includes('dataSource=CDN')

        commit('setDataSource', isCDN ? 'CDN' : 'API')
    }

    commit('setIsMapDataUpdateNeeded', false);
    const response = state.dataSourceType === 'CDN' ? await fetchMapDataFromCDN(mapId) : await fetchMapDataFromApi(mapId);
    dispatch('pushDataToState', response.data);

    // in case if data loaded from CDN save last modified date
    if(state.dataSourceType === 'CDN' && response && response.headers && response.headers['last-modified']){
        commit('setLastModified', Date.parse(response.headers['last-modified']));
        commit('setLastModifiedCheckedAt', Date.now())
    }

    commit('setAppLoaded', true);
    return Promise.resolve(response.data)
};

export const changeLocale = ({ commit, getters }, newLocale) => {
    if( getters.getActiveLocale !== newLocale) {
        // UX hack: allow lang dropdown to close before applying major recalculations
        setTimeout(() => {
            commit('setLocale', newLocale)
            Vue.i18n.set(newLocale)
        },10)
    }
};
export const pushDataToState = ({ commit, dispatch , state}, data) => {
    commit('setMap', data.map);
    commit('pushMapUnits', data.units);
    commit('pushMapModels', data.models);
    commit('pushMapObjects', data.objects);
    commit('pushCategories', data.categories);
    commit('pushUnfilteredMapObjects', data.objects);
    commit('setActiveLocale', data.map.meta.language);
    commit('setDefaultLocale', data.map.meta.language);

    // set terminal object ID by terminal code
    if(state.terminalCode && data.terminals){
        const activeTerminal = data.terminals.find( t => t.code === state.terminalCode)
        // previously we hold unit name but now terminal object id is there
        activeTerminal && commit('setTerminalId', parseInt(activeTerminal.unit,10));
    }

    const translations = {};

    data.translations.forEach(item => {
        Object.keys(item.meta).forEach(el => {
            if(item.meta[el]){
                if(!translations[el])
                    translations[el] = {};

                translations[el][item.name] = item.meta[el]
            }

        })
    });

    if( _.has(data,'map.meta.languages') ) {
        data.map.meta.languages.forEach(el => {
            Vue.i18n.add(el, translations[el]);
        });
    }

    Vue.i18n.set(data.map.meta.language);
    Vue.i18n.fallback('en');
    dispatch('setAppLoaded', true);
};

export const setAppLoaded = ({ commit }, value) => {
    commit('setAppLoaded', value)
};

export const sendSms = ({getters}, data) => {
    return postTelNumber({ ...data, lang: getters.getActiveLocale})
};

export const sendEvent = ({ getters }, data) => {
    if(process.env.VUE_APP_STATISTICS_ENABLED !== 'true') return Promise.resolve();

    const eventData = {
        ...getters.getBaseEventData,
        datetime: dateConverter(),
        url:document.location.href,
        ...data
    };
    console.debug('StatisticsEvent:', eventData)
    const firehose = new AWS.Firehose();

    const params = {
        DeliveryStreamName: process.env.VUE_APP_STATISTICS_GENERAL_STREAM,
        Record: {
            Data: JSON.stringify(eventData),
        },
    };

    return firehose.putRecord(params, (err, d) => {
        if (err) throw(err)
    })
};

export const sendSearchEvent = ({ dispatch, getters}, searchParams) => {
    const event = {
        event_category:"ui",
        event:"search",
        ...searchParams
    };
    dispatch('sendEvent', event)
};

export const sendCategoryClickEvent = ({ dispatch, getters}, category ) => {
    const cat = typeof category === 'number' ? getters.getListItem(category) : category
    if(cat)
        dispatch('sendEvent', {
            event_category:"ui",
            event:"click",

            target:'category',
            target_type: getters.getCategoryRoot(cat).id,
            target_id: cat.id,
        })
}

export const sendObjectClickEvent = ({ dispatch, getters}, object ) => {
    const obj = typeof object === 'number' ? getters.getObject(object) : object
    if(obj)
        dispatch('sendEvent', {
            event_category:"ui",
            event:"click",

            target:'object',
            target_type: obj.type,
            target_id: obj.id,
        })
}

export const sendObjectHoverEvent = ({ dispatch, getters}, object ) => {
    const obj = typeof object === 'number' ? getters.getObject(object) : object
    if(obj)
        dispatch('sendEvent', {
            event_category:"ui",
            event:"hover",

            target:'object',
            target_type: obj.type,
            target_id: obj.id,
        })
}

export const sendGuideEvent = ({ dispatch, getters}, {guide, params} ) => {
    const obj = guide?.to?.id ? getters.getObject(guide.to.id) : {}

    dispatch("sendEvent", {
        tid: guide.from.id || guide.from,
        event_category: "ui",
        event: "guide",
        target: 'object',
        target_type: obj.type,
        target_id: guide.to.id || guide.to,
        value1: 'fast',
        ...params
    })
};

export const sendGuideStepEvent = ({ state, dispatch, getters}, { stepIndex, eventCategory }) => {
    const obj = getters.getObject(_.get(state.activePath,'to'))

    const eventData = {
        tid: _.get(state.activePath,'from.id') || _.get(state.activePath,'from'),
        event_category: eventCategory || 'ui',
        event: "guide_step",
        target: 'object',
        target_type: obj.type,
        target_id: _.get(state.activePath,'to.id') || _.get(state.activePath,'to'),
        value1: _.get(state.activePath, 'steps.length', 0) - stepIndex - 1,
        value2: _.get(state.activePath, `steps[${stepIndex}].type`, 'undefined').toLowerCase(), //step type
    }
    dispatch("sendEvent", eventData)
}

export const registerAWSIdentity = () =>{
    // Registrer cognito identy
    AWS.config.region = 'eu-central-1'; // Region
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: process.env.VUE_APP_STATISTICS_IDENTY_POOL_ID,
    });
}
export const generateSessionId = ({commit}) => {
    commit('setSessionId', new Date().getTime())
}

export const changeLocaleAtStart = ({commit}) => {
    // This function check language setting device or browser and change locale after
    let language = window.navigator ? (window.navigator.language || window.navigator.systemLanguage || window.navigator.userLanguage) : "en";
    language = language.substr(0, 2)
    if (language === 'en' || language === 'se' || language === 'fi') {
        commit('setLocale', language)
        Vue.i18n.set(language)
    } else {
        return
        //commit('setLocale', 'en')
        //Vue.i18n.set('en')
    }
}

export const checkDoesMapDataUpdateNeeded = async ({dispatch, commit, state}, mapId) => {
    const lastModified = await fetchMapDataLastModified(mapId);
    const isUpdateNeeded = state.lastModified !== lastModified;
    commit('setIsMapDataUpdateNeeded', isUpdateNeeded);
    commit('setLastModifiedCheckedAt', Date.now());
    return isUpdateNeeded;
}
